import {
  DraggableChildrenFn,
  DraggableRubric,
  DraggableStateSnapshot,
  DraggingStyle,
  DropAnimation,
  NotDraggingStyle,
} from '@hello-pangea/dnd';
import { useMemo } from 'react';
import { ChipBox } from '@/components/question/view/primitives/shared/dnd/ChipBox';
import { getChipDom } from '@/utils/questions/dnd/dndNodes';
import { Helpers, useDragDropAnswerContext } from '@/components/question/view/primitives/shared/dnd/DragDropAnswerContext';
import { BANK_ID, DnDToken } from '@/utils/questions/dnd/types';

function findDisplacedOccupant(
  snapshot: DraggableStateSnapshot,
  rubric: DraggableRubric,
  { capacityOf, tokensOf }: Pick<Helpers, 'capacityOf' | 'tokensOf'>,
): DnDToken | undefined {
  const over = snapshot.draggingOver;
  if (!snapshot.isDropAnimating || !over || over === BANK_ID) return undefined;
  if (capacityOf(over) === 'multiple') return undefined;

  return tokensOf(over).find(item => String(item.id) !== rubric.draggableId);
}

function getSwapStyle(
  occupant: DnDToken,
  baseStyle: DraggingStyle | NotDraggingStyle | undefined,
  dropAnimation: DropAnimation | null,
): DraggingStyle | undefined {
  if (!dropAnimation || !baseStyle || !('left' in baseStyle)) return undefined;

  const occupantNode = getChipDom(occupant.id);
  if (!occupantNode) return undefined;

  const occupantRect = occupantNode.getBoundingClientRect();
  const occupantTransform = getComputedStyle(occupantNode).transform;
  const matrix =
    occupantTransform && occupantTransform !== 'none'
      ? new DOMMatrixReadOnly(occupantTransform)
      : undefined;

  const targetLeft = occupantRect.left - (matrix?.m41 ?? 0);
  const targetTop = occupantRect.top - (matrix?.m42 ?? 0);
  const { curve, duration } = dropAnimation;

  return {
    ...baseStyle,
    transform: `translate(${targetLeft - baseStyle.left}px, ${
      targetTop - baseStyle.top
    }px)`,
    transition: `all ${curve} ${duration}s`,
  };
}

export function renderChipClone({
  getToken,
  ...helpers
}: Helpers): DraggableChildrenFn {
  return (provided, snapshot, rubric) => {
    const token = getToken(rubric.draggableId);

    if (!token) {
      return (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
        />
      );
    }

    const occupant = findDisplacedOccupant(snapshot, rubric, helpers);
    const swapStyle =
      occupant &&
      getSwapStyle(
        occupant,
        provided.draggableProps.style,
        snapshot.dropAnimation,
      );

    const cloneProvided = swapStyle
      ? {
          ...provided,
          draggableProps: { ...provided.draggableProps, style: swapStyle },
        }
      : provided;

    return (
      <ChipBox token={token} provided={cloneProvided} snapshot={snapshot} />
    );
  };
}

export function useChipClone() {
  const { getToken, capacityOf, tokensOf } = useDragDropAnswerContext();
  return useMemo(
    () => renderChipClone({ getToken, capacityOf, tokensOf }),
    [getToken, capacityOf, tokensOf],
  );
}
